/**
 * Token Provider Service
 *
 * Handles authentication token exchange between the web component and host application
 * using the Custom Event pattern. This service requests tokens via 'wc-token-request'
 * events and receives them via 'wc-token-response' events.
 *
 * @packageDocumentation
 */

import { Injectable, inject, signal } from '@angular/core';
import { TokenResponse } from '../models/api.model';
import { LoggerService } from './logger.service';
import { JwtDecoderService } from './jwt-decoder.service';

/**
 * Service responsible for obtaining authentication tokens from the host application.
 *
 * Uses the Custom Event-based token provider pattern:
 * 1. Dispatches 'wc-token-request' to request a token
 * 2. Host application handles request and responds with 'wc-token-response'
 * 3. Token is cached until expiry for subsequent requests
 *
 * @example
 * ```typescript
 * const tokenProvider = inject(TokenProviderService);
 *
 * // Get token (may come from cache or request new)
 * const token = await tokenProvider.getToken();
 *
 * // Clear cached token (e.g., on logout)
 * tokenProvider.clearToken();
 * ```
 */
@Injectable({ providedIn: 'root' })
export class TokenProviderService {
  private readonly logger = inject(LoggerService);
  private readonly jwtDecoder = inject(JwtDecoderService);

  /**
   * Timeout in milliseconds for token requests.
   * If no response is received within this time, the request fails.
   */
  private readonly TOKEN_TIMEOUT_MS = 5000;

  /**
   * Cached token value (null if no token cached)
   */
  private readonly _token = signal<string | null>(null);

  /**
   * Cached token expiry timestamp (Unix milliseconds)
   */
  private readonly _tokenExpiry = signal<number | null>(null);

  /**
   * Currently pending token request promise (for deduplication)
   */
  private _pendingRequest: Promise<string> | null = null;

  /**
   * Public readonly signal exposing the current token.
   * Use getToken() for async retrieval with caching.
   */
  readonly token = this._token.asReadonly();

  /**
   * Retrieves an authentication token.
   *
   * Returns cached token if still valid, otherwise requests a new token
   * from the host application via Custom Events.
   *
   * @returns Promise resolving to the JWT token string
   * @throws Error if token request times out or fails
   *
   * @example
   * ```typescript
   * try {
   *   const token = await tokenProvider.getToken();
   *   console.log('Got token:', token.substring(0, 20) + '...');
   * } catch (error) {
   *   console.error('Token request failed:', error.message);
   * }
   * ```
   */
  async getToken(): Promise<string> {
    // Return cached token if available (no expiry check if expiry not set)
    const cachedToken = this._token();
    const expiry = cachedToken ? this.jwtDecoder.getExpirationMs(cachedToken) : null;

    this.logger.debug('TokenProvider', 'getToken called', {
      cachedToken: cachedToken ? 'SET' : 'NULL',
      expiry
    });

    // If we have a cached token with a known, non-expired expiry, return it.
    // A null expiry means no reliable expiry was provided — force a refresh.
    if (cachedToken && (expiry !== null && Date.now() < expiry)) {
      this.logger.debug('TokenProvider', 'Returning cached token');
      return cachedToken;
    }

    // Token is expired or has no expiry — clear the cache and request a refresh
    const isExpiredRefresh = !!cachedToken;
    if (isExpiredRefresh) {
      this.logger.debug('TokenProvider', 'Cached token expired or missing expiry, requesting refresh from host');
      this._token.set(null);
    }

    // If a request is already pending, wait for it
    if (this._pendingRequest) {
      return this._pendingRequest;
    }

    // Request a new token (or a refresh if the previous token expired)
    this._pendingRequest = this.requestToken(isExpiredRefresh);

    try {
      return await this._pendingRequest;
    } finally {
      this._pendingRequest = null;
    }
  }

  /**
   * Dispatches a token request and waits for the response.
   *
   * @returns Promise resolving to the token string
   * @throws Error if request times out or response contains an error
   */
  private requestToken(refresh = false): Promise<string> {
    return new Promise((resolve, reject) => {
      // Set up timeout
      const timeoutId = setTimeout(() => {
        window.removeEventListener('wc-token-response', handler);
        reject(new Error('Token request timed out'));
      }, this.TOKEN_TIMEOUT_MS);

      // Handler for token response
      const handler = (event: Event): void => {
        clearTimeout(timeoutId);
        window.removeEventListener('wc-token-response', handler);

        const customEvent = event as CustomEvent<TokenResponse & { error?: string }>;
        const { token, expiresAt, error } = customEvent.detail || {};

        // Check for error response
        if (error) {
          reject(new Error(error));
          return;
        }

        // Validate token exists
        if (!token) {
          reject(new Error('No token provided in response'));
          return;
        }

        // Cache the token — use host-provided expiresAt, or fall back to
        // decoding the exp claim directly from the JWT itself.
        const resolvedExpiry = expiresAt ?? this.jwtDecoder.getExpirationMs(token);
        this._token.set(token);
        this._tokenExpiry.set(resolvedExpiry);

        resolve(token);
      };

      // Listen for response
      window.addEventListener('wc-token-response', handler);

      // Dispatch request event — include refresh:true so the host can
      // trigger re-authentication rather than returning the expired token
      window.dispatchEvent(
        new CustomEvent('wc-token-request', {
          bubbles: true,
          composed: true,
          detail: { refresh }
        })
      );
    });
  }

  /**
   * Sets a token directly (bypasses Custom Event exchange).
   * Use this when the host provides the token via attribute.
   * 
   * @param token - The JWT token string
   * @param expiresAt - Optional expiry timestamp (Unix milliseconds)
   */
  setToken(token: string, expiresAt?: number): void {
    this.logger.debug('TokenProvider', 'setToken called', { tokenLength: token?.length });
    this._token.set(token);
    this._tokenExpiry.set(expiresAt ?? null);
    this.logger.debug('TokenProvider', 'Token cached', { value: this._token() ? 'SET' : 'NULL' });
  }

  /**
   * Clears the cached token.
   * Call this when the user logs out or token should be invalidated.
   */
  clearToken(): void {
    this._token.set(null);
    this._tokenExpiry.set(null);
    this._pendingRequest = null;
  }

  /**
   * Clears the cached token and immediately requests a fresh one from the host
   * with `refresh: true`, prompting the host to re-authenticate the user.
   *
   * Use this when a server-side 401 indicates the current token is invalid.
   *
   * @returns Promise resolving to the new JWT token string
   * @throws Error if the refresh request times out or fails
   */
  async refreshToken(): Promise<string> {
    this._token.set(null);
    this._tokenExpiry.set(null);
    this._pendingRequest = null;
    this._pendingRequest = this.requestToken(true);
    try {
      return await this._pendingRequest;
    } finally {
      this._pendingRequest = null;
    }
  }

  /**
   * Checks if a valid (non-expired) token is currently cached.
   *
   * @returns true if a valid token is cached
   */
  hasValidToken(): boolean {
    const cachedToken = this._token();
    const expiry = this._tokenExpiry();
    return !!(cachedToken && expiry && Date.now() < expiry);
  }
}
